import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, LoaderCircle } from "lucide-react";
import type { ReportExportFormat } from "@multi-chat/shared";
import { Button } from "../../components/ui/button/index.js";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover/index.js";

export function ReportExportMenu({
  exporting,
  disabled,
  onExport,
  testId,
}: {
  exporting: boolean;
  disabled?: boolean;
  onExport: (format: ReportExportFormat) => void;
  testId?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || exporting}
          data-testid={testId ?? "reports-export-menu"}
          className="gap-1.5"
        >
          {exporting ? (
            <LoaderCircle size={14} className="animate-spin" aria-hidden="true" />
          ) : (
            <Download size={14} aria-hidden="true" />
          )}
          {exporting ? t("reports.export.exporting") : t("reports.export.label")}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-40 p-1">
        <button
          type="button"
          className={`flex w-full rounded-md px-2.5 py-1.5 text-start text-[0.8125rem] hover:bg-muted/60${
            exporting ? " pointer-events-none opacity-50" : ""
          }`}
          data-testid="reports-export-csv"
          disabled={exporting}
          onClick={() => {
            setOpen(false);
            onExport("csv");
          }}
        >
          {t("reports.export.csv")}
        </button>
        <button
          type="button"
          className={`flex w-full rounded-md px-2.5 py-1.5 text-start text-[0.8125rem] hover:bg-muted/60${
            exporting ? " pointer-events-none opacity-50" : ""
          }`}
          data-testid="reports-export-xlsx"
          disabled={exporting}
          onClick={() => {
            setOpen(false);
            onExport("xlsx");
          }}
        >
          {t("reports.export.excel")}
        </button>
      </PopoverContent>
    </Popover>
  );
}
